/**
 * OverlayBtn widget - a generic, layout-independent overlay button
 */

import LoggingBase from '../../base/loggingbase';
import ItmMgr from '../../gui/ItmMgr';
import Utils from '../../utils/Utils';
import Validator from '../../utils/Validator';

/** the CSS classes used to nail the button to a corner of the parent element */
const CORNER_CLASSES = {
    tl: 'overlayBtnTopLeft',
    tr: 'overlayBtnTopRight',
    bl: 'overlayBtnBottomLeft',
    br: 'overlayBtnBottomRight'
};

const DEFAULT_CORNER = 'tr';

/** default button size in px (matches the .overlayBtn CSS width/height) */
const DEFAULT_SIZE = 20;
/** smallest button size in px when shrunk to fit a short row above the primary */
const MIN_SIZE = 12;
/** how much smaller (px) a font icon is drawn than the button box */
const ICON_SIZE_INSET = 5;
/** smallest font-icon size in px */
const ICON_MIN_FONT = 9;

/** CSS marker class of repository-driven dialog labels (see LblPlc.CSS_REPOSITORY_LABEL) */
const REPOSITORY_LABEL_CLASS = 'repositoryLabel';

/**
 * class OverlayBtn - a generic (semi-)transparent overlay button that is nailed to a
 * corner of its parent element and becomes fully opaque on mouse hover. It is independent
 * of the surrounding (SWT) layout and reports clicks back to the backend.
 */
export default class OverlayBtn extends LoggingBase {

    /**
     * constructs a new instance
     * @param {*} properties properties provided by the framework
     */
    constructor(properties) {
        super('widgets.ovlbtn.OverlayBtn');
        Utils.bindAll(this, [ 'onRender' ]);
        this.ready = false;
        this.parent = rap.getObject(properties.parent);
        this._corner = DEFAULT_CORNER;
        this._references = { primary: '', secondaryLeft: '', secondaryRight: '' };
        // ResizeObserver that re-applies the placement on layout changes (lazily created)
        this._ro = null;
        // the overlay button element
        this.element = document.createElement('div');
        this.element.classList.add('overlayBtn');
        this.element.classList.add(CORNER_CLASSES[this._corner]);
        // the current icon element (created from an ImgObj descriptor via ItmMgr)
        this.iconElm = null;
        // the icon's natural font size (for font based icons), used to reset the size regime
        this._iconBaseFontSize = '';
        this.element.addEventListener('click', (evt) => this._onClick(evt));
        this.parent.append(this.element);
        rap.on('render', this.onRender);
        if ( this.isDebugEnabled() ) {
            this.debug('OverlayBtn created.');
        }
    }

    /**
     * @inheritdoc
     * @override
     */
    doDestroy() {
        this.ready = false;
        if ( this._ro ) {
            this._ro.disconnect();
            this._ro = null;
        }
        const elm = this.element;
        this.element = null;
        this.iconElm = null;
        delete this.parent;
        if ( elm && elm.parentNode ) {
            elm.parentNode.removeChild(elm);
        }
        super.doDestroy();
    }

    /**
     * destroys the widget (called by the framework)
     */
    destroy() {
        super.destroy();
    }

    /**
     * called at the end of the "render" phase
     */
    onRender() {
        rap.off('render', this.onRender);
        this._attachOverlay();
        this.ready = true;
        // references may have arrived before the DOM was ready - apply them now
        this._applyReferences();
    }

    /**
     * moves the overlay element out of the (0x0, clipped) RAP host wrapper and into the
     * host's container - the actual, sized parent element - so that the corner positioning
     * anchors against a real box instead of the zero-size host point.
     */
    _attachOverlay() {
        const elm = this.element;
        if ( !elm ) {
            return;
        }
        // the RAP-created host wrapper of our custom composite; it is a 0x0, "overflow:
        // hidden" element positioned at the origin of the dialog - see also XWdg
        const host = elm.parentNode;
        if ( !(host instanceof HTMLElement) ) {
            return;
        }
        const container = host.parentNode;
        if ( container instanceof HTMLElement ) {
            // make sure the container establishes a positioning context, otherwise our
            // "position: absolute" would anchor against some outer ancestor
            const pos = window.getComputedStyle(container).position;
            if ( !pos || pos === 'static' ) {
                container.style.position = 'relative';
            }
            container.appendChild(elm);
            if ( this.isDebugEnabled() ) {
                this.debug('Overlay re-parented to the dialog container.');
            }
        } else {
            // fall back to at least un-clipping the host so the button becomes visible
            host.style.overflow = 'visible';
            this.warn('No container element found - overlay stays inside the host wrapper.');
        }
    }

    /**
     * sets the icon
     * @param {*} args an ImgObj descriptor (font based icon, bitmap or SVG); null/undefined
     *        removes the icon
     */
    setIcon(args) {
        if ( !this.element ) {
            return;
        }
        // drop the previous icon element (if any)
        if ( this.iconElm && this.iconElm.parentNode ) {
            this.iconElm.parentNode.removeChild(this.iconElm);
        }
        this.iconElm = null;
        this._iconBaseFontSize = '';
        if ( args && Validator.isObject(args) ) {
            // let ItmMgr build the proper DOM element from the ImgObj descriptor
            this.iconElm = ItmMgr.getInst().creImg(args, 0, false);
            if ( this.iconElm ) {
                // remember the icon's natural font size (font based icons are an <i> tag with
                // an inline font-size) so the size-regime adjustment in _applyReferences can be
                // applied relative to - and reset back to - this baseline
                this._iconBaseFontSize = this.iconElm.style.fontSize || '';
                this.element.appendChild(this.iconElm);
            }
        }
    }

    /**
     * adjusts the icon's font size to the current button size. Font based icons (an <i> tag)
     * would look visually too large if kept at a fixed size in a small button, so the font
     * size follows the button size (a few px smaller), clamped between {@link #ICON_MIN_FONT}
     * and the icon's natural font size.
     * @param {Number} size the current button size in px
     */
    _adjustIconFontSize(size) {
        const icon = this.iconElm;
        if ( !icon || (icon.tagName !== 'I') ) {
            // only font based icons (an <i> tag) have a meaningful font size
            return;
        }
        const base = parseInt(this._iconBaseFontSize, 10);
        const upper = (Number.isFinite(base) && (base > 0)) ? base : DEFAULT_SIZE;
        const fsz = Math.min(upper, Math.max(ICON_MIN_FONT, size - ICON_SIZE_INSET));
        icon.style.fontSize = fsz + 'px';
    }

    /**
     * sets the tooltip
     * @param {String} args the tooltip text
     */
    setTooltip(args) {
        if ( this.element ) {
            const tooltip = Validator.isString(args) ? args : '';
            if ( tooltip ) {
                this.element.setAttribute('title', tooltip);
            } else {
                this.element.removeAttribute('title');
            }
        }
    }

    /**
     * sets the corner the button is nailed to
     * @param {String} args the corner code ("tl", "tr", "bl", "br")
     */
    setCorner(args) {
        if ( this.element ) {
            const corner = CORNER_CLASSES[args] ? args : DEFAULT_CORNER;
            if ( corner !== this._corner ) {
                this.element.classList.remove(CORNER_CLASSES[this._corner]);
                this.element.classList.add(CORNER_CLASSES[corner]);
                this._corner = corner;
            }
        }
    }

    /**
     * sets the placement references that let the button position itself relative to other
     * controls instead of sticking to a fixed corner.
     * <ul>
     *   <li><b>primary</b> - the anchor control: the button aligns to its horizontal edge on
     *       the side given by the current corner (see {@link #setCorner});</li>
     *   <li><b>secondary</b> (optional) - the control that may push the button outward if it
     *       extends past the primary's anchored edge, and whose row the button vertically
     *       centers on.</li>
     * </ul>
     * Example use case: a "reset" button for a row of quick-filter comboboxes - to the right
     * of the right-most combobox if the row leaves room there, otherwise to the left of the
     * left-most combobox.
     * @param {*} args object with the widget ids "primary", "secondaryLeft" and "secondaryRight"
     */
    setReferences(args) {
        const refs = this._references;
        refs.primary = Validator.ensureString(args.primary);
        refs.secondaryLeft = Validator.ensureString(args.secondaryLeft);
        refs.secondaryRight = Validator.ensureString(args.secondaryRight);
        if ( this.ready ) {
            // (re)apply immediately
            this._applyReferences();
        }
    }

    /**
     * resolves a RAP widget id to its top-level DOM element
     * @param {String} id the RAP widget id
     * @returns {HTMLElement|null} the DOM element or null if it cannot be resolved
     */
    _resolveElement(id) {
        if ( !Validator.isString(id) ) {
            return null;
        }
        const rw = Utils.getRapWidget(id);
        return rw ? rw.element : null;
    }

    /**
     * places the button relative to its references (if resolvable). Horizontally the button
     * prefers the right of the right-most secondary control: if the row leaves enough room
     * between that control and the container's right edge, the button sits there (small gap);
     * otherwise it sits to the left of the left-most secondary control (same gap), clamped to
     * the container's left edge (it may then overlap whatever sits further left - accepted).
     * Vertically it centers on the secondary controls' row but is never allowed to reach into
     * the primary's area (e.g. a table directly below the filter row); if it would overlap, it
     * is lifted to sit just above the primary. If the secondary references cannot be resolved
     * the button keeps its default corner placement.
     */
    _applyReferences() {
        const elm = this.element;
        if ( !elm || !this.ready ) {
            return;
        }
        const container = elm.parentNode;
        if ( !(container instanceof HTMLElement) ) {
            return;
        }
        const leftElm = this._resolveElement(this._references.secondaryLeft);
        const rightElm = this._resolveElement(this._references.secondaryRight);
        if ( !leftElm || !rightElm ) {
            // without the secondary anchors we cannot position the button - keep corner placement
            return;
        }
        // the primary (e.g. the table) is optional: it only constrains the vertical placement
        const primaryElm = this._resolveElement(this._references.primary);
        const cRect = container.getBoundingClientRect();
        const leftRect = leftElm.getBoundingClientRect();
        const rightRect = rightElm.getBoundingClientRect();
        const MARGIN = 2;
        const GAP = 18;

        const style = elm.style;
        style.right = 'auto';
        style.bottom = 'auto';

        // the primary's bounding rect (if any); it provides the horizontal edge the button
        // aligns to (e.g. the table's right edge) and the top edge it must stay above
        const primRect = primaryElm ? primaryElm.getBoundingClientRect() : null;
        const primaryTop = primRect ? (primRect.top - cRect.top) : null;

        // size: orient on the reference control's (e.g. combobox) height, 1px higher, so the
        // button matches the row's controls instead of a fixed size; bounded by DEFAULT_SIZE
        // (upper) and MIN_SIZE (lower). If the band above the primary (e.g. a table directly
        // below a short filter row) cannot fit the button plus the gap, shrink it further so it
        // never reaches into the primary. Setting the size explicitly keeps this deterministic
        // across re-runs (offsetWidth would otherwise reflect a prior shrink).
        let size = Math.round(rightRect.height) + 1;
        size = Math.min(DEFAULT_SIZE, Math.max(MIN_SIZE, size));
        if ( primaryTop !== null ) {
            const band = primaryTop - MARGIN;
            if ( band < size ) {
                size = Math.max(MIN_SIZE, band);
            }
        }
        style.width = size + 'px';
        style.height = size + 'px';
        this._adjustIconFontSize(size);
        const btnW = size;
        const btnH = size;

        // the secondary controls may have labels sitting next to them (e.g. a "View" label left
        // of its combobox) that are not part of the reference list; include such repository
        // labels so the button anchors to the true edge of the whole cluster, not just the combo
        const edges = this._clusterEdges(container, leftRect, rightRect);

        // horizontal: if the row leaves room to the right of the cluster, place the button
        // there - aligned to the primary's (e.g. the table's) right edge, so it lines up with
        // the main control; but if the cluster sticks out past that edge, place the button just
        // past the cluster instead. If there is no room on the right, go to the left of the
        // cluster (clamped to the container's left).
        const roomOnRight = (cRect.right - MARGIN) - edges.right;
        let leftPx;
        if ( roomOnRight >= (GAP + btnW) ) {
            // right edge the button aligns to: the primary's right edge (fall back to just past
            // the cluster if there is no primary)
            let btnRight = primRect ? (primRect.right - MARGIN) : (edges.right + GAP + btnW);
            // if aligning to the primary edge would not leave the gap to the cluster (or the
            // cluster even sticks out past it), place the button just past the cluster
            if ( (edges.right + GAP + btnW) > btnRight ) {
                btnRight = edges.right + GAP + btnW;
            }
            btnRight = Math.min(btnRight, cRect.right - MARGIN);
            leftPx = (btnRight - cRect.left) - btnW;
        } else {
            leftPx = (edges.left - cRect.left) - GAP - btnW;
            leftPx = Math.max(MARGIN, leftPx);
        }
        style.left = leftPx + 'px';

        // vertical: center on the secondary controls' row, but never let the button reach into
        // the primary's area (e.g. the table below the filter row). If the centered position
        // would overlap the primary, the button is lifted so it sits just above the primary,
        // leaving a small gap between its bottom edge and the primary's top edge (clamped to
        // the container's top). Thanks to the size shrink above, the button always fits.
        let top = (rightRect.top - cRect.top) + Math.max(0, (rightRect.height - btnH) / 2);
        if ( primaryTop !== null ) {
            if ( (top + btnH + MARGIN) > primaryTop ) {
                top = primaryTop - btnH - MARGIN;
            }
        }
        style.top = Math.max(0, top) + 'px';

        this._observeReferences(container, leftElm, rightElm, primaryElm);
        if ( this.isDebugEnabled() ) {
            this.debug(`Applied references: left=${style.left}, top=${style.top} (roomOnRight=${Math.round(roomOnRight)}, side=${roomOnRight >= (GAP + btnW) ? 'right' : 'left'}).`);
        }
    }

    /**
     * computes the left and right edge of the whole control cluster: the secondary reference
     * controls plus any repository-driven dialog labels (see LblPlc) that share the secondaries'
     * row. This lets the button anchor to the true edge of a "label + combobox" cluster instead
     * of just the combobox. Repository labels are recognized by their marker CSS class.
     * @param {HTMLElement} container the container element
     * @param {DOMRect} leftRect the left-most secondary control's bounding rect
     * @param {DOMRect} rightRect the right-most secondary control's bounding rect
     * @returns {{left: Number, right: Number}} the cluster's left and right viewport x-edges
     */
    _clusterEdges(container, leftRect, rightRect) {
        let left = leftRect.left;
        let right = rightRect.right;
        // the row band spanned by the secondary controls (with a small tolerance); a label
        // counts as "on the row" if its vertical center lies within this band
        const bandTop = Math.min(leftRect.top, rightRect.top) - 2;
        const bandBottom = Math.max(leftRect.bottom, rightRect.bottom) + 2;
        const labels = container.getElementsByClassName(REPOSITORY_LABEL_CLASS);
        for ( let i = 0; i < labels.length; ++i ) {
            const r = labels[i].getBoundingClientRect();
            const center = r.top + (r.height / 2);
            if ( (center >= bandTop) && (center <= bandBottom) ) {
                if ( r.left < left ) {
                    left = r.left;
                }
                if ( r.right > right ) {
                    right = r.right;
                }
            }
        }
        return { left: left, right: right };
    }

    /**
     * lazily installs a ResizeObserver so the placement follows layout changes (e.g. when a
     * neighbouring area is resized and a reference control changes size/position)
     * @param {...(HTMLElement|null)} elements the elements to observe (nullish entries ignored)
     */
    _observeReferences(...elements) {
        if ( this._ro || !window.ResizeObserver ) {
            return;
        }
        this._ro = new ResizeObserver(() => {
            if ( this.alive && this.ready ) {
                this._applyReferences();
            }
        });
        for ( const el of elements ) {
            if ( el instanceof HTMLElement ) {
                this._ro.observe(el);
            }
        }
    }

    /**
     * adds or removes a CSS class
     * @param {*} args arguments
     */
    setCssClass(args) {
        if ( this.element ) {
            const csc = args.csc || '';
            if ( Validator.isString(csc) && csc ) {
                if ( args.add ) {
                    this.element.classList.add(csc);
                } else {
                    this.element.classList.remove(csc);
                }
            }
        }
    }

    /**
     * "click" listener
     * @param {MouseEvent} e the click event
     */
    _onClick(e) {
        const button = e.button || 0;
        this._nfySrv('clicked', { button: button });
    }

    /**
     * notifies the web server
     * @param {String} code notification code
     * @param {*} par notification parameters
     */
    _nfySrv(code, par) {
        if ( this.ready ) {
            const tms = Date.now();
            const param = {};
            param.cod = code;
            param.par = par;
            param.tms = tms;
            rap.getRemoteObject(this).notify('OVERLAYBTN_NFY', param);
        }
    }


    /** register custom widget type */
    static register() {
        rap.registerTypeHandler('psawidget.OverlayBtn', {
            factory : function(properties) {
                return new OverlayBtn(properties);
            },
            destructor : 'destroy',
            properties : [ 'icon', 'tooltip', 'corner', 'references' ],
            methods : [ 'setCssClass' ],
            events : [ 'OVERLAYBTN_NFY' ]
        } );
    }
}
